/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors, Typography } from '@zapier/design-system';
import { MouseEventHandler, ReactNode } from 'react';

export type CustomBrandButtonProps = {
  children: ReactNode;
  variant?: 'primary' | 'primary-alt' | 'secondary' | 'zapconnect';
  size?: 'small' | 'medium';
  href?: string;
  onClick?: MouseEventHandler;
  showGoogleIcon?: boolean;
};

const focusStyles = css({
  outline: 'none',

  '&::before': {
    content: '""',
    position: 'absolute',
    pointerEvents: 'none',
    top: '-6px',
    right: '-6px',
    bottom: '-6px',
    left: '-6px',
    border: `3px solid ${Colors.zapier}`,
    borderRadius: '31px',
  },

  '&[data-variant="primary-alt"]': {
    '&::before': {
      borderColor: Colors.earth,
    },
  },

  '&[data-variant="secondary"]': {
    '&::before': {
      borderColor: Colors.earth,
    },
  },

  '&[data-variant="zapconnect"]': {
    '&::before': {
      borderColor: Colors.earth,
      borderRadius: '22px',
    },
  },
});

const Styles = labelStyles('CustomBrandButton', {
  root: css({
    minWidth: '272px',
    height: '50px',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '6px',
    position: 'relative',
    padding: '8px 20px',
    boxSizing: 'border-box',
    fontFamily: Typography.base.fontFamily,
    fontSize: '18px',
    fontWeight: 600,
    lineHeight: 1.5,
    textDecoration: 'none',
    color: Colors.neutral100,
    backgroundColor: Colors.zapier,
    border: `1px solid ${Colors.zapier}`,
    borderRadius: '25px',
    transition: 'background-color 250ms, border-color 250ms',

    '&[data-size="small"]': {
      minWidth: 'auto',
      height: '44px',
    },

    '&[data-variant="primary-alt"]': {
      backgroundColor: Colors.earth,
      color: Colors.neutral100,
      borderColor: Colors.earth,
    },

    '&[data-variant="secondary"]': {
      backgroundColor: Colors.neutral100,
      color: Colors.earth,
      borderColor: Colors.earth,
    },

    '&[data-variant="zapconnect"]': {
      backgroundColor: Colors.earth,
      color: Colors.white,
      borderColor: Colors.earth,
      borderRadius: '18px',
    },

    '&:hover:not([disabled]), &:active:not([disabled])': {
      backgroundColor: '#D24304',
      borderColor: '#D24304',

      '&[data-variant="primary-alt"]': {
        backgroundColor: Colors.night,
        borderColor: Colors.night,
      },

      '&[data-variant="secondary"]': {
        backgroundColor: Colors.neutral100,
        borderColor: Colors.earth,
        borderWidth: '2px',
      },

      '&[data-variant="zapconnect"]': {
        backgroundColor: '#DDFF76',
        borderColor: '#DDFF76',
        color: Colors.earth,
      },
    },
    '&:focus-visible:not(:active)': focusStyles,
    // For browsers that don't support :focus-visible, apply focus styles
    '@supports not selector(:focus-visible)': {
      '&:focus:not(:active)': focusStyles,
    },
  }),

  icon: css({
    display: 'flex',
    padding: '4px',
    backgroundColor: Colors.neutral100,
    borderRadius: '8px',
  }),
});

export function CustomBrandButton(props: CustomBrandButtonProps) {
  return (
    <a
      css={Styles.root}
      href={props.href}
      data-variant={props.variant}
      data-size={props.size}
      onClick={props.onClick}
    >
      {props.showGoogleIcon && (
        <span css={Styles.icon}>
          <svg
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.2041 8.34882V12.3006H15.6958C15.4546 13.5715 14.731 14.6476 13.6456 15.3712L16.9573 17.9408C18.8868 16.1597 20 13.5437 20 10.4361C20 9.71257 19.9351 9.01677 19.8145 8.34892L10.2041 8.34882Z"
              fill="#4285F4"
            />
            <path
              d="M4.48548 12.1463L3.73857 12.7181L1.09473 14.7774C2.77377 18.1077 6.21509 20.4083 10.2039 20.4083C12.959 20.4083 15.2688 19.4992 16.9572 17.9408L13.6455 15.3712C12.7364 15.9834 11.5768 16.3545 10.2039 16.3545C7.55088 16.3545 5.29677 14.5641 4.48966 12.1522L4.48548 12.1463Z"
              fill="#34A853"
            />
            <path
              d="M1.09454 5.63083C0.398843 7.00369 0 8.55289 0 10.2041C0 11.8553 0.398843 13.4045 1.09454 14.7773C1.09454 14.7865 4.4898 12.1428 4.4898 12.1428C4.28571 11.5306 4.16509 10.8812 4.16509 10.204C4.16509 9.5267 4.28571 8.87739 4.4898 8.26514L1.09454 5.63083Z"
              fill="#FBBC05"
            />
            <path
              d="M10.2042 4.0631C11.707 4.0631 13.0428 4.58257 14.1096 5.58444L17.0316 2.66238C15.2598 1.01119 12.9593 0 10.2042 0C6.2153 0 2.77377 2.29129 1.09473 5.63083L4.48987 8.26536C5.29688 5.85345 7.5511 4.0631 10.2042 4.0631Z"
              fill="#EA4335"
            />
          </svg>
        </span>
      )}
      {props.children}
    </a>
  );
}
